 import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { Button } from '../components/ui/button';
import {
  CalendarIcon,
  UserIcon,
  StarIcon,
  DollarSignIcon,
  ShoppingCartIcon,
  HomeIcon,
  ClipboardListIcon,
  LayoutDashboardIcon,
  PieChartIcon,
  MessageSquareIcon,
  PhoneCallIcon,
  CalendarPlusIcon,
  BookOpenIcon,
} from 'lucide-react';
import { toast } from 'react-toastify';

const SidebarItem = ({ icon, label, to }) => {
  const location = useLocation();
  const isActive = location.pathname === to || (to === "/expert" && location.pathname === "/expert");

  return (
    <Link
      to={to}
      className={`flex items-center gap-3 p-2 rounded-md text-sm font-medium ${
        isActive ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-700 hover:bg-white'
      }`}
    >
      <span className="w-5 h-5">{icon}</span>
      <span>{label}</span>
    </Link>
  );
};

const ExpertDashboard = () => {
  const { logout, accessToken: token, user, setUser } = useAuth();
  const [isOnline, setIsOnline] = useState(user?.is_online || false);

  useEffect(() => {
    if (user) {
      setIsOnline(user.is_online);
    }
  }, [user]);

  const handleOnlineToggle = async () => {
    try {
      const response = await fetch(`https://quirkleai.com/api/me/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ is_online: !isOnline }),
      });

      if (!response.ok) {
        throw new Error('Failed to update online status');
      }

      const updatedUser = await response.json();
      setIsOnline(updatedUser.is_online);
      setUser(updatedUser);
    } catch (error) {
      console.error("Error toggling online status:", error);
      toast.error("Failed to update online status");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row">
      <aside className="w-full md:w-64 bg-[#fdf3e8] border-r p-4 space-y-2 text-sm sticky top-0 h-screen">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">🔅 Expert Dashboard</h2>

        <div
          onClick={handleOnlineToggle}
          className={`flex items-center rounded-full px-4 py-2 transition-all duration-300 w-[120px] text-white font-semibold cursor-pointer 
            ${isOnline ? 'bg-green-500 justify-start' : 'bg-red-500 justify-end'}`}
        >
          {isOnline && <div className="w-5 h-5 bg-white rounded-full mr-2"></div>}
          <span>{isOnline ? 'Online' : 'Offline'}</span>
          {!isOnline && <div className="w-5 h-5 bg-white rounded-full ml-2"></div>}
        </div>

        <SidebarItem to="/expert" icon={<HomeIcon size={16} />} label="Home" />
        <SidebarItem to="/expert/bookings" icon={<ClipboardListIcon size={16} />} label="Bookings" />
        <SidebarItem to="/expert/messages" icon={<MessageSquareIcon size={16} />} label="Priority DM" />
        <SidebarItem to="/expert/testimonials" icon={<StarIcon size={16} />} label="Testimonials" />
        <SidebarItem to="/expert/calendar" icon={<CalendarIcon size={16} />} label="Calendar" />
        <SidebarItem to="/expert/services" icon={<LayoutDashboardIcon size={16} />} label="Services" />
        <SidebarItem to="/expert/analytics" icon={<PieChartIcon size={16} />} label="Analytics" />
        <SidebarItem to="/expert/availability" icon={<CalendarPlusIcon size={16} />} label="Availability" />
        <SidebarItem to="/expert/marketing" icon={<PhoneCallIcon size={16} />} label="Marketing" />
        <SidebarItem to="/expert/payments" icon={<DollarSignIcon size={16} />} label="Payments" />
        <SidebarItem to="/expert/checkout" icon={<ShoppingCartIcon size={16} />} label="Checkout" />
        <SidebarItem to="/expert/profile" icon={<UserIcon size={16} />} label="Profile" />
        <SidebarItem to="/expert/achievements" icon={<StarIcon size={16} />} label="Achievements" />
        <SidebarItem to="/expert/resources" icon={<BookOpenIcon size={16} />} label="Resources" />
        <div className="pt-4 mt-4 border-t">
          <Button
            className="w-full bg-red-600 hover:bg-red-700 text-white text-sm py-2"
            onClick={logout}
          >
            🚪 Logout
          </Button>
        </div>
      </aside>

      <main className="flex-1 p-6">
        <Outlet />
      </main>
    </div>
  );
};

export default ExpertDashboard;
